import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { useAuth } from '../context/AuthContext';
import {
  Heart, MessageSquare, ShoppingBag, Shield, Users, AlertTriangle,
  CheckCircle, XCircle, Scale, Sparkles, ArrowRight
} from 'lucide-react';

const rules = [
  {
    icon: Heart,
    title: 'Be Respectful',
    description: 'Treat all community members with kindness and respect. Personal attacks, harassment, and discrimination are not tolerated.',
    dos: ['Use constructive feedback', 'Acknowledge different perspectives', 'Support fellow creators'],
    donts: ['Harass or bully others', 'Use hate speech', 'Share personal information without consent']
  },
  {
    icon: Sparkles,
    title: 'Create Original Content',
    description: 'Share your own work and give proper credit when featuring others. Plagiarism undermines our creative community.',
    dos: ['Post your original creations', 'Credit collaborators', 'Respect copyright'],
    donts: ['Claim others\' work as your own', 'Repost without permission', 'Violate intellectual property']
  },
  {
    icon: ShoppingBag,
    title: 'Marketplace Ethics',
    description: 'Maintain honesty in all marketplace transactions. Accurate descriptions and fair pricing build trust.',
    dos: ['Describe products accurately', 'Respond to buyer questions', 'Honor your commitments'],
    donts: ['Mislead about products', 'Price gouge', 'Sell prohibited items']
  },
  {
    icon: MessageSquare,
    title: 'Constructive Communication',
    description: 'Engage thoughtfully in discussions. Quality conversations help everyone grow.',
    dos: ['Share helpful insights', 'Ask thoughtful questions', 'Stay on topic'],
    donts: ['Spam or self-promote excessively', 'Derail conversations', 'Post repetitive content']
  },
  {
    icon: Shield,
    title: 'Protect Privacy',
    description: 'Respect everyone\'s privacy and personal boundaries. What\'s shared in private should stay private.',
    dos: ['Keep private messages confidential', 'Ask before sharing others\' info', 'Report suspicious activity'],
    donts: ['Dox or expose personal info', 'Screenshot private conversations', 'Stalk or track members']
  },
  {
    icon: Users,
    title: 'Community First',
    description: 'Help build a positive community by reporting issues and supporting moderation efforts.',
    dos: ['Report violations', 'Help newcomers', 'Contribute positively'],
    donts: ['Ignore harmful behavior', 'Create drama', 'Circumvent moderation']
  }
];

const prohibited = [
  'Adult/explicit content',
  'Illegal goods or services',
  'Hate speech or discrimination',
  'Scams or fraudulent schemes',
  'Malware or harmful content',
  'Spam or excessive self-promotion'
];

export default function RulesPage() {
  const { user } = useAuth();

  return (
    <div className="min-h-screen bg-background font-sans">
      {/* Header - only show if not logged in (outside Layout) */}
      {!user && (
        <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/80 border-b border-black/5">
          <div className="max-w-6xl mx-auto flex items-center justify-between h-14 px-4 md:px-8">
            <Link to="/" className="font-serif text-lg tracking-tight">
              <span className="text-obsidian">Susan David</span>
              <span className="text-gold ml-1">Community</span>
            </Link>
            <Link to="/auth">
              <Button className="rounded-sm text-xs uppercase tracking-wider h-9 px-6 bg-obsidian hover:bg-gold hover:text-obsidian transition-colors duration-300">
                Sign In
              </Button>
            </Link>
          </div>
        </header>
      )}

      <div className="animate-in max-w-4xl mx-auto px-4 py-8 md:py-12" data-testid="rules-page">
        {/* Hero */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-gold/10 text-gold px-4 py-1.5 rounded-full text-xs font-medium mb-4">
            <Scale size={12} /> Community Guidelines
          </div>
          <h1 className="font-serif text-3xl md:text-4xl mb-4">Community Rules</h1>
          <p className="text-muted-foreground text-sm md:text-base max-w-xl mx-auto">
            These guidelines help us maintain a safe, creative, and supportive environment for all members.
          </p>
        </div>

        {/* Quick Summary */}
        <Card className="border-gold/30 bg-gold/5 rounded-sm mb-10">
          <CardContent className="py-6">
            <div className="flex items-start gap-4">
              <div className="bg-gold/20 p-2 rounded-sm shrink-0">
                <AlertTriangle size={20} className="text-gold" />
              </div>
              <div>
                <h3 className="font-medium text-sm mb-2">The Golden Rule</h3>
                <p className="text-sm text-muted-foreground">
                  Treat others as you would like to be treated. Be kind, be honest, and contribute positively to our creative community.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Detailed Rules */}
        <div className="space-y-6 mb-12">
          {rules.map((rule, index) => (
            <Card key={index} className="border-border/40 rounded-sm overflow-hidden" data-testid={`rule-card-${index}`}>
              <CardHeader className="pb-3 bg-mist/50">
                <CardTitle className="flex items-center gap-3 font-serif text-base">
                  <div className="bg-obsidian text-white p-2 rounded-sm">
                    <rule.icon size={16} />
                  </div>
                  {rule.title}
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-4">
                <p className="text-sm text-muted-foreground mb-4">{rule.description}</p>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <h4 className="text-xs font-medium text-green-600 flex items-center gap-1.5 mb-2">
                      <CheckCircle size={12} /> Do
                    </h4>
                    <ul className="space-y-1">
                      {rule.dos.map((item, i) => (
                        <li key={i} className="text-xs text-muted-foreground flex items-start gap-2">
                          <span className="text-green-500 mt-0.5">+</span> {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <h4 className="text-xs font-medium text-red-600 flex items-center gap-1.5 mb-2">
                      <XCircle size={12} /> Don't
                    </h4>
                    <ul className="space-y-1">
                      {rule.donts.map((item, i) => (
                        <li key={i} className="text-xs text-muted-foreground flex items-start gap-2">
                          <span className="text-red-500 mt-0.5">-</span> {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Prohibited Content */}
        <Card className="border-red-200 bg-red-50/50 rounded-sm mb-12" data-testid="prohibited-section">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 font-serif text-base text-red-700">
              <XCircle size={18} /> Prohibited Content
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-red-600/80 mb-4">
              The following content is strictly prohibited and will result in immediate action:
            </p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {prohibited.map((item, i) => (
                <div key={i} className="flex items-center gap-2 text-xs text-red-700">
                  <div className="w-1.5 h-1.5 bg-red-400 rounded-full" />
                  {item}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Enforcement */}
        <Card className="border-border/40 rounded-sm mb-12">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 font-serif text-base">
              <Shield size={18} className="text-gold" /> Enforcement
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-4">
              Violations of these rules may result in the following actions:
            </p>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded text-xs font-medium">Warning</div>
                <p className="text-xs text-muted-foreground">First-time or minor violations receive a warning with guidance.</p>
              </div>
              <div className="flex items-start gap-3">
                <div className="bg-orange-100 text-orange-700 px-2 py-0.5 rounded text-xs font-medium">Suspension</div>
                <p className="text-xs text-muted-foreground">Repeated violations may result in temporary account suspension.</p>
              </div>
              <div className="flex items-start gap-3">
                <div className="bg-red-100 text-red-700 px-2 py-0.5 rounded text-xs font-medium">Ban</div>
                <p className="text-xs text-muted-foreground">Severe or repeated violations result in permanent account termination.</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* CTA */}
        <div className="text-center bg-obsidian text-white py-10 px-6 rounded-sm">
          <h2 className="font-serif text-xl mb-3">Ready to Join?</h2>
          <p className="text-white/60 text-sm mb-6 max-w-md mx-auto">
            By joining Susan David Community, you agree to follow these guidelines and help us build a positive space for creators.
          </p>
          <Link to={user ? '/feed' : '/auth'}>
            <Button
              data-testid="rules-cta-btn"
              className="rounded-sm text-xs uppercase tracking-wider h-10 px-8 bg-gold text-obsidian hover:bg-white transition-colors"
            >
              {user ? 'Go to Feed' : 'Join the Community'} <ArrowRight size={14} className="ml-2" />
            </Button>
          </Link>
        </div>
      </div>

      {/* Footer - only show if not logged in */}
      {!user && (
        <footer className="border-t border-border/40 py-8 mt-12">
          <div className="max-w-6xl mx-auto px-4 text-center">
            <p className="text-xs text-muted-foreground">Susan David Community. A premium creative platform.</p>
          </div>
        </footer>
      )}
    </div>
  );
}
